import {
  AssociateFileWithAccountMutationVariables,
  useAssociateFileWithAccountMutation,
} from 'graphql/bindings/AdminBindings'
import {
  Box,
  Button,
  Card,
  FormModal,
  Theme,
  TimesIcon,
  Typography,
  ValueAutocompleteField,
  makeStyles,
  useSnackbar,
} from '@forgeglobal/design-framework'
import { Form } from 'react-final-form'
import Cookie from 'js-cookie'
import EnvVars from 'util/EnvVars'
import React, { useEffect } from 'react'

interface FormShape {
  files: string[]
}

interface Tags {
  label: string
  value: string
}

interface UploadDocumentsModalProps {
  hideModal: () => void
  removeDocument: (name: string) => void
  files: File[]
  pageSource: 'account' | 'issuer'
  accountId: string
  tags: Tags[]
}

export const UploadDocumentsModal: React.FC<UploadDocumentsModalProps> = ({
  hideModal,
  files,
  accountId,
  tags,
  removeDocument,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const [mutate] = useAssociateFileWithAccountMutation()

  async function postData(url = '', file: File) {
    const body = new FormData()

    body.append('size', file.size.toString())
    body.append('name', file.name)
    body.append('file', file)

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'x-csrf-token': Cookie.get('x-csrf-token') ?? '',
      },
      body,
    })

    return await response.text()
  }

  const useStyles = makeStyles((theme: Theme) => {
    return {
      icon: {
        cursor: 'pointer',
        marginLeft: theme.spacing(2),
      },
      outerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2),
      },
      innerContainer: {
        padding: theme.spacing(0, 2),
        display: 'flex',
        flexDirection: 'column',
      },
      dropdown: {
        marginLeft: theme.spacing(2),
      },
      text: {
        marginBottom: theme.spacing(1),
      },
    }
  })

  const onSubmit = async (values: FormShape) => {
    const uploadUrl = `${EnvVars.API_URL}/upload`
    files.forEach(async (file, idx) => {
      const variables: AssociateFileWithAccountMutationVariables = {
        accountId,
        fileSerial: await postData(uploadUrl, file),
        tags: [values.files[idx]],
      }
      mutate({ variables }).then(() => {
        hideModal()
        enqueueSnackbar(`Uploading document ${file.name} ...`)
      })
    })
    hideModal()
  }

  useEffect(() => {
    // hide the modal if there are no documents to assign types to
    if (files.length === 0) {
      hideModal()
    }
  })

  const classes = useStyles()

  return (
    <FormModal open hideModal={hideModal} title="Choose Document Type">
      <Form<FormShape>
        onSubmit={onSubmit}
        initialValues={{ files: [] }}
        validate={(values: FormShape) => {
          if (values.files.length !== files.length) {
            return { ERROR: 'Please assign a tag to each file you want to upload' } as { [key: string]: string }
          }
        }}
        render={({ handleSubmit }) => {
          return (
            <form onSubmit={handleSubmit}>
              {files.map((file, idx) => {
                return (
                  <Card className={classes.outerContainer} key={`files[${idx}]`}>
                    <div className={classes.innerContainer}>
                      <Typography className={classes.text}>{file.name}</Typography>
                      <ValueAutocompleteField name={`files[${idx}]`} label={'Type (required)'} options={tags} />
                    </div>
                    <TimesIcon className={classes.icon} onClick={() => removeDocument(file.name)} />
                  </Card>
                )
              })}
              <Box my={2}>
                <Button variant="contained" onClick={hideModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  Save
                </Button>
              </Box>
            </form>
          )
        }}
      />
    </FormModal>
  )
}
